import { z } from "zod";

import { activityEventSchema } from "./activityEvents.ts";
import { APPLICATION_ID } from "./constants.ts";

/**
 * Who an event is about: a signed-in user working inside one organization (an isolated sandbox).
 * Stamped by the API from the session, never taken from the browser payload.
 */
export const subjectSchema = z.object({
	userId: z.string().min(1).max(200),
	organizationId: z.string().min(1).max(200)
});

export type Subject = z.infer<typeof subjectSchema>;

/** An activity event after the API has authenticated and enriched it: the message on the queue. */
export const contextEventSchema = z.object({
	app: z.literal(APPLICATION_ID),
	subject: subjectSchema,
	receivedAt: z.iso.datetime(),
	event: activityEventSchema
});

export type ContextEvent = z.infer<typeof contextEventSchema>;

/** Every message the context service consumes. A union so new kinds can be added without a new queue. */
export const contextMessageSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("event"), payload: contextEventSchema })
]);

export type ContextMessage = z.infer<typeof contextMessageSchema>;

/**
 * The key a context snapshot is stored under. Per user *per organization*: orgs are isolated
 * sandboxes, so what a user does in one org never informs the advisor in another.
 */
export const subjectIdFor = (subject: Subject): string => `org:${subject.organizationId}:user:${subject.userId}`;
